import React from "react";
import * as Icons from "lucide-react";
import { AlertTriangle, ArrowRight, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { fullSymptoms, symptomsFaqs, clinic } from "../data/mock";

const urgent = ["Vomiting blood", "Black, tarry stools", "Severe abdominal swelling", "Confusion or drowsiness", "Deep jaundice", "High fever with pain"];
const diseases = [
  { name: "Fatty Liver", link: "/fatty-liver-treatment-varanasi" },
  { name: "Liver Cirrhosis", link: "/liver-cirrhosis-treatment-varanasi" },
  { name: "Hepatitis B & C", link: "/hepatitis-treatment-varanasi" },
  { name: "Jaundice & Infection", link: "/jaundice-treatment-varanasi" },
];

const LiverSymptoms = () => {
  return (
    <div>
      <SEO path="/liver-disease-symptoms" faqs={symptomsFaqs} />
      <PageHero breadcrumb="Liver Symptoms"
        title="Liver Disease Symptoms"
        subtitle="Recognise the warning signs early. Know when to consult a liver specialist in Varanasi." />

      <section className="py-16 sm:py-20">
        <div className="container-app max-w-4xl text-center">
          <SectionHeading eyebrow="Awareness" title="Why Liver Symptoms Shouldn't Be Ignored" />
          <p className="text-slate-600 leading-relaxed text-lg">
            The liver is a resilient organ and can function even when partly damaged — which means symptoms often appear only when disease has advanced. Recognising the early warning signs and acting promptly can prevent serious, irreversible liver damage.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Common Symptoms" title="Signs of Liver Disease" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {fullSymptoms.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Activity;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                  <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 grid place-items-center mb-4"><Icon size={22} /></div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-1">{s.label}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading center={false} eyebrow="Emergency" title="Symptoms That Need Immediate Attention" />
            <div className="space-y-3">
              {urgent.map((u, i) => (
                <div key={i} className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
                  <AlertTriangle size={20} className="text-red-500 shrink-0" />
                  <span className="text-sm font-medium text-red-700">{u}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Possible Causes" title="Diseases Behind These Symptoms" />
            <div className="grid sm:grid-cols-2 gap-4">
              {diseases.map((d, i) => (
                <Link key={i} to={d.link} className="group flex items-center justify-between bg-sky-50/60 border border-sky-100 rounded-xl px-5 py-4 hover:bg-sky-50 transition-colors">
                  <span className="text-sm font-semibold text-slate-700">{d.name}</span>
                  <ArrowRight size={16} className="text-sky-500 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
            <div className="mt-6 bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
              <h3 className="font-heading font-semibold text-slate-900 mb-2 flex items-center gap-2"><Stethoscope size={20} className="text-sky-500" /> When to Consult</h3>
              <p className="text-slate-600 text-sm leading-relaxed">If symptoms persist for more than two weeks or you have abnormal liver reports, consult {clinic.doctorName} for an accurate diagnosis and personalized treatment plan.</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={symptomsFaqs} title="Liver Symptoms — Frequently Asked Questions" />
      <AppointmentCTA title="Noticing Symptoms? Get Checked Today" />
    </div>
  );
};

export default LiverSymptoms;
